import React, { FC, useEffect, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';
import { useHistory } from 'react-router-dom';
import {
  ListItemText,
  ListItem,
  Input,
  InputAdornment,
  IconButton,
  Box,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Popover,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import CancelIcon from '../../../../assets/icons/cancel-icon';
import useStyles from './styles';
import { AppState } from '../../../../store';
import { FileType } from '../../../../store/transactions/types';
import { QueryParameterKeys } from '../../../../globals/paramter';
import { parametersAsObject, urlUpdate } from '../../../../globals/utils';
import { getAllFileTypes } from '../../../../store/transactions/actions';
import FilterIcon from '../../../../assets/icons/filter-icon';
import useUpdateEffect from '../../../../globals/hooks/use-update-effect';

type RowType = { index: number; style: object; data: FileType[] };

type FilterProperties = {
  title?: string;
};

const regRelatedFiles = [
  '10088', // ECM Info V2
  '10213', // ECMInfo V3
  '10199', // Registration Info V2
  '10346', // Registration Info v3
  '10161', // Set Registration Info
  '10128', // SMH Adjustment Response
  '10078', // SMH Adjustment
  '10065', // SMHInfo
  '10519', // ECM Info (protobuf)
  '10554', // Combined Info File
  '10474', // CAT Telematics Registration
  '10696', // Combined Registration Report V2
  '10062', // Remote Flash
  '10502', // Remote Flash response
  '10030', // Deployment File
];
const vimsRelatedFiles = [
  '10208',
  '10101',
  '10100_CL_CNF',
  '10100_ER_CNF',
  '10103',
  '10104',
  '10102',
  '10100_DL_CNF',
  '10106',
  '10110',
  '10109',
  '10107',
  '10008',
  '10012',
  '10014',
  '10010',
  '10009',
  '10013',
  '10011',
  '10201',
  '10336',
  '10337',
];

const getTextWidth = (textContent: string | null) => {
  const text = document.createElement('span');
  document.body.append(text);
  text.style.font = 'lato-regular, Helvetica, Arial, sans-serif';
  text.style.fontWeight = '400';
  text.style.fontSize = `${14}px`;
  text.style.height = 'auto';
  text.style.width = 'auto';
  text.style.position = 'absolute';
  text.style.whiteSpace = 'no-wrap';
  if (textContent) text.innerHTML = textContent;
  const width = Math.ceil(text.clientWidth);
  text.remove();
  return width;
};

const FilterHeader: FC<FilterProperties> = (properties: FilterProperties) => {
  const { title } = properties;
  const classes = useStyles();
  const [anchorElement, setAnchorElement] = React.useState<HTMLElement | null>(null);
  const fileTypes = useSelector((state: AppState) => state.transactions.allFileTypes);
  const [changedMade, setChangesMade] = useState<Boolean>(false);
  const [regRelatedSelected, setRegRelatedSelected] = useState<Boolean>(false);
  const [vimsRelatedSelected, setVimsRelatedSelected] = useState<Boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<FileType[] | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [filterBy, setFilteredBy] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();
  const parameters: URLSearchParams = new URLSearchParams(history.location.search);
  const fileTypeIdFilters = parameters.get(QueryParameterKeys.fileTypeIdFilters) || undefined;
  const excludeFileTypeIdFilters = parameters.get(QueryParameterKeys.excludeFileTypeIdFilters) || undefined;
  const vimsData = parameters.get(QueryParameterKeys.vimsData) || undefined;
  const open = Boolean(anchorElement);
  const disabledButtons = selectedOptions.join(',') === fileTypeIdFilters;

  const listReference = useRef<VariableSizeList>(null);
  const reRenderList = () => listReference?.current?.resetAfterIndex(0);
  const fileTypesArray = fileTypes.map((fileType: FileType) => fileType.fileTypeId);

  const toggleSelectValue = (fileTypeId: any) => {
    const selectedOptionsCopy = [...selectedOptions];
    const index: number = selectedOptionsCopy.indexOf(fileTypeId);
    if (index === -1) {
      setSelectedOptions([...selectedOptionsCopy, fileTypeId]);
    }
    else {
      selectedOptionsCopy.splice(index, 1);
      setSelectedOptions(selectedOptionsCopy);
    }
    setChangesMade(true);
  };
  const Row = ({ index, style, data }: RowType): any => {
    const item = data[index];
    const selected = selectedOptions.includes(`${item.fileTypeId}`);
    return (
      <ListItem
        className={classes.listItem}
        onKeyPress={() => toggleSelectValue(item.fileTypeId)}
        onClick={() => toggleSelectValue(item.fileTypeId)}
        style={style}
        button
        id={`${index}`}
        selected={selected}
      >
        <ListItemText classes={{ root: classes.listItemText }} primary={item.fileTypeName} />
      </ListItem>
    );
  };

  const handleClose = () => {
    setAnchorElement(null);
  };
  const handleClick = (event: any) => {
    setAnchorElement(event.currentTarget);
  };

  const updateFilteredList = (value: string) => {
    const filtered = fileTypes.filter((option: FileType) =>
      `${option.fileTypeName} ${option.fileTypeId}`
        .toLocaleLowerCase()
        .includes(value.toLocaleLowerCase()),
    );
    setFilteredBy(value);
    setFilteredOptions(filtered);
    reRenderList();
  };

  const handleInputChange = (event: any) => {
    const { value } = event.target;
    updateFilteredList(value);
  };
  const updateFilteredOption = (selectedFilterOptions: string[]) => {
    const unSelectedFilteredArray = fileTypes.filter((item: FileType) => !selectedFilterOptions.includes(item.fileTypeId));
    const removedArray = fileTypes.filter((item: FileType) => selectedFilterOptions.includes(item.fileTypeId));
    setFilteredOptions([...removedArray, ...unSelectedFilteredArray]);
  }
  const updateOptions = () => {
    if (!filteredOptions && fileTypes.length !== 0) {
      setFilteredOptions(fileTypes);
    }
    if (fileTypeIdFilters) {
      const urlFilters: Array<string> = fileTypeIdFilters ? fileTypeIdFilters.split(',') : [];
      setSelectedOptions(urlFilters);
      updateFilteredOption(urlFilters);
      return;
    }
    if (excludeFileTypeIdFilters) {
      const urlFilters: Array<string> = excludeFileTypeIdFilters ? excludeFileTypeIdFilters.split(',') : [];
      setSelectedOptions(fileTypesArray.filter((fileType: string) => !urlFilters.includes(fileType)));
    }
  };

  const fetchOptions = () => {
    if (fileTypes.length === 0) dispatch(getAllFileTypes({ limit: 1000, skip: 0 }));
  };

  useEffect(() => updateOptions(), [fileTypes, fileTypeIdFilters, excludeFileTypeIdFilters, vimsData]);
  useUpdateEffect(() => fetchOptions(), [open]);

  const selectedOptionsInverted = () => {
    const selectedOptionsInverted: any = fileTypesArray.filter((fileType: string) => !selectedOptions.includes(fileType));
    return selectedOptionsInverted;
  };

  const applyCustomFilters = () => {
    const input: { [key: string]: string } =
      selectedOptions.length > 125
        ? {
          excludeFileTypeIdFilters: selectedOptionsInverted().join(','),
        }
        : {
          fileTypeIdFilters: selectedOptions.join(','),
        };
    urlUpdate(history, parametersAsObject(parameters), input);
    handleClose();
  };
  const handleRegRelated = () => {
    const addRegToExisting = [...selectedOptions, ...regRelatedFiles];
    const dedupedSelected = [...new Set(addRegToExisting)];
    setSelectedOptions(dedupedSelected);
    setChangesMade(true);
    setRegRelatedSelected(true);
    updateFilteredOption(dedupedSelected);
  };
  const handleVimsRelated = () => {
    const addVimsToExisting = [...selectedOptions, ...vimsRelatedFiles];
    const dedupedSelected = [...new Set(addVimsToExisting)];
    setSelectedOptions(dedupedSelected);
    setChangesMade(true);
    setVimsRelatedSelected(true);
    updateFilteredOption(dedupedSelected);
  };

  const handleUnselect = () => {
    if (selectedOptions.length > 0) setChangesMade(true);
    setSelectedOptions([]);
    setRegRelatedSelected(false);
    setVimsRelatedSelected(false);
  };

  const handleSelectAll = () => {
    setSelectedOptions(fileTypes.map((fileType: FileType) => fileType.fileTypeId));
    setChangesMade(true);
  };

  const getSize = (index: number) => {
    const name = filteredOptions && filteredOptions[index].fileTypeName;
    const width = getTextWidth(name);
    if (width > 304) {
      if (width > 550) return 90;
      return 50;
    }
    return 30;
  };
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onKeyPress={handleClick}
        className={classes.filterContainer}
        onClick={handleClick}
        id="navbarFilter"
      >
        <Box width="100%" display="flex" justifyContent="center" alignItems="center">
          <Box display="flex" alignItems="center">
            <FilterIcon className={classes.icon} />
            <Typography variant="body1">{title || 'Filter'}</Typography>
          </Box>
        </Box>
      </div>
      {open && (
        <Popover
          id="filter-component"
          open={open}
          anchorEl={anchorElement}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <div className={classes.popOverContents}>
            <Box px={2} py={2}>
              <Typography variant="h3" className={classes.menuTitle}>
                Custom Filter
              </Typography>
              <Box pt={2} pb={1} display="flex">
                <Button
                  className={classes.smallButton}
                  disabled={!!regRelatedSelected}
                  onClick={handleRegRelated}
                  size="small"
                  variant="outlined"
                >
                  Add Reg Related
                </Button>
                <Button
                  className={classes.smallButton}
                  disabled={!!vimsRelatedSelected}
                  onClick={handleVimsRelated}
                  size="small"
                  variant="outlined"
                >
                  VIMS
                </Button>
                <Button
                  onClick={handleUnselect}
                  className={classes.smallButton}
                  size="small"
                  variant="outlined"
                  disabled={selectedOptions.length === 0}
                >
                  Unselect All
                </Button>
                <Button
                  onClick={handleSelectAll}
                  className={classes.smallButton}
                  size="small"
                  variant="outlined"
                  disabled={selectedOptions.length === fileTypes.length}
                >
                  Select All
                </Button>
              </Box>
              <Typography>{selectedOptions.length} selected</Typography>
            </Box>

            <Input
              className={classes.input}
              id="filter-quick-search"
              onChange={handleInputChange}
              value={filterBy}
              startAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => updateFilteredList('')}
                    size="large"
                  >
                    <CancelIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
            <div id="filterOptions" className={classes.container}>
              {fileTypes.length > 0 && filteredOptions ? (
                <AutoSizer>
                  {({ height, width }) => (
                    <VariableSizeList
                      ref={listReference}
                      height={height}
                      width={width}
                      itemCount={filteredOptions.length}
                      itemSize={getSize}
                      itemData={filteredOptions}
                    >
                      {Row}
                    </VariableSizeList>
                  )}
                </AutoSizer>
              ) : (
                <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                  <CircularProgress color="secondary" thickness={2} />
                </Box>
              )}
            </div>
            <Divider />
            {fileTypeIdFilters === undefined || excludeFileTypeIdFilters === undefined ? (
              <Box display="flex" p={1}>
                <Button
                  onClick={applyCustomFilters}
                  className={classes.button}
                  variant="contained"
                  color="secondary"
                  disabled={disabledButtons}
                >
                  Apply
                </Button>
              </Box>
            ) : (
              <Box display="flex" p={1}>
                <Button
                  className={classes.button}
                  disabled={!changedMade}
                  onClick={applyCustomFilters}
                  variant="contained"
                  color="secondary"
                >
                  Save Changes
                </Button>
              </Box>
            )}
          </div>
        </Popover>
      )}
    </>
  );
};
export { FilterHeader };
